import { useEffect } from 'react';
import Highlight from '@tiptap/extension-highlight';
import { CharacterCount, type CharacterCountOptions } from '@tiptap/extensions';
import { type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { type Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { logger } from '@zep/core-logger';
import { MathInline } from '@/shared/features/rich-text';
import { useEditMode } from '../../focus/useIsEditMode';
import { openEquationEditorModal } from '../EquationEditorModal';

/**
 * 입력 중 글자수 제한. 기본 `CharacterCount` 는 친 글자를 그대로 세고, 수식 노드는 leaf 라
 * 공백 1자로 센다 — 수식은 1자, 공백은 친 만큼이다. 저장 검증(zod·서버)은 `countQuizText` 로
 * 공백을 접어 세므로, 에디터가 같거나 더 엄격하다. 공백까지 접어 세면 센 글자 수와 문서 위치가
 * 어긋나 붙여넣기 잘라내기가 통째로 거부되므로 에디터는 그대로 센다.
 */
const QuizCharacterCount = CharacterCount.extend<CharacterCountOptions & {
  /**
   * 문단 사이를 셀 글자. 기본 카운터는 문단 사이를 세지 않는데, 저장값(`extractBareText`)은
   * 문단 사이에 줄바꿈을 넣어 1자로 센다. 짝맞추기처럼 줄바꿈을 그대로 저장하는 칸은 `'\n'` 을
   * 넘겨 에디터와 저장 검증이 같은 수를 내게 한다.
   */
  blockSeparator?: string;
}>({
  addOptions() {
    return {
      ...(this.parent?.() as CharacterCountOptions),
      blockSeparator: undefined
    };
  },
  onBeforeCreate(event) {
    this.parent?.(event);
    const {
      blockSeparator,
      textCounter
    } = this.options;
    if (blockSeparator === undefined) return;
    this.storage.characters = options => {
      const node = options?.node ?? this.editor.state.doc;
      return textCounter(node.textBetween(0, node.content.size, blockSeparator, ' '));
    };
  },
  // 기본 플러그인은 처음 열 때 한도를 넘은 문서의 앞부분을 지운다(appendTransaction). 저장된
  // 내용이 에디터를 여는 것만으로 사라지면 안 되므로 그 동작은 빼고, 입력 차단과 붙여넣기
  // 잘라내기(filterTransaction)만 남긴다. 한도를 넘은 문서는 줄이는 편집만 허용된다.
  // ⚠️ 공식 옵션이 없어 CharacterCount 의 플러그인 구성(플러그인 1개, 그 spec 의
  // appendTransaction·filterTransaction, storage.characters 를 filterTransaction 이 호출)에
  // 기댄다. @tiptap/extensions 를 올릴 때 한도를 넘은 문서를 열어도 잘리지 않는지, 한도에서
  // 타이핑이 막히고 붙여넣기가 잘리는지 확인한다.
  addProseMirrorPlugins() {
    return (this.parent?.() ?? []).map(plugin => new Plugin({
      ...plugin.spec,
      appendTransaction: undefined
    }));
  }
});

/**
 * 퀴즈 생성 시 싱글톤 텍스트 에디터를 제공하는 hook
 */
export const useBuildTextEditor = ({
  content,
  onUpdate,
  characterLimit = 300,
  characterCounter,
  blockSeparator,
  formulaMaxLength
}: {
  content?: string;
  onUpdate?: (content: string) => void;
  characterLimit?: number;
  characterCounter?: (text: string) => number;
  /** 문단 사이를 셀 글자. `QuizCharacterCount` 의 같은 옵션 참고. */
  blockSeparator?: string;
  /** 수식 하나의 LaTeX 원문 상한(짝맞추기·순서맞추기 보기). */
  formulaMaxLength?: number;
}) => {
  const {
    onEditModeChange
  } = useEditMode();
  const editor = useEditor({
    extensions: [StarterKit, QuizCharacterCount.configure({
      limit: characterLimit,
      mode: 'textSize',
      ...(characterCounter && {
        textCounter: characterCounter
      }),
      blockSeparator
    }), Highlight.configure({
      multicolor: true
    }),
    // Custom MathInline extension with onClick handler
    MathInline.configure({
      onClick: async (node: ProseMirrorNode, pos: number, editor: Editor) => {
        const latex = await openEquationEditorModal({
          initialValue: node?.attrs?.latex ?? '',
          maxLength: formulaMaxLength
        });
        if (latex) {
          editor.chain().setNodeSelection(pos).updateAttributes('mathInline', {
            latex
          }).focus().run();
          editor.commands.focus('end');
        }
        logger.track('build_addformula', {
          location: 'question'
        });
      }
    })],
    immediatelyRender: false,
    content,
    onUpdate: ({
      editor
    }) => {
      const html = editor.getHTML();
      onUpdate?.(editor.isEmpty ? '' : html);
    },
    parseOptions: {
      preserveWhitespace: 'full'
    },
    editorProps: {
      attributes: {
        class: 'w-full border-none focus-within:outline-none focus:outline-none focus-visible:outline-none',
        'data-no-focus-visible': 'true'
      }
    }
  });

  // content가 변경되었을 때 에디터 내용을 업데이트
  useEffect(() => {
    if (editor && content !== undefined) {
      const currentContent = editor.getHTML();
      if (currentContent !== content) {
        // 폼 값을 에디터로 옮기는 동기화라 onUpdate 로 폼에 되쓰지 않는다(TipTap 3 기본값은 emit).
        // 되쓰면 index 경로가 바뀌는 커밋에서 잠깐 보인 빈 값이 다른 보기를 지운다.
        editor.commands.setContent(content, {
          emitUpdate: false
        });
      }
    }
  }, [content, editor]);
  useEffect(() => {
    if (editor) {
      editor.on('focus', () => {
        onEditModeChange(true);
      });
    }
    // return () => {
    //   editor?.off('focus');
    //   editor?.off('blur');
    // };
  }, [editor, onEditModeChange]);
  return {
    editor
  };
};